/**
 * Logique PURE du verrouillage de conversation (testable sans React/Convex).
 *
 * Le verrouillage empêche deux chatters de répondre au même fan en même temps :
 * ouvrir une conversation pose un verrou à son nom ; les autres voient « tenu
 * par X » et ne peuvent plus l'ouvrir. Les rôles bypass (ceo / manager +
 * superadmin) passent outre et ne verrouillent jamais.
 *
 * Ces helpers ne font AUCUN I/O : ils transforment (verrou, now, identité,
 * rôle) → décision d'affichage. Le wiring Convex/React vit dans
 * `useConversationLocks.ts` + `ConversationLockController.tsx`.
 */
import { atom } from "jotai";

import type { Layout, LayoutNode } from "@/shell/layout";

/**
 * TTL d'un verrou sans heartbeat (doit matcher `LOCK_TTL_MS` côté Convex,
 * conversationLocks.ts). Au-delà sans rafraîchissement → périmé.
 */
export const LOCK_TTL_MS = 50_000;

/**
 * Période de heartbeat du ConversationLockController. < TTL pour tolérer au
 * moins 2 battements manqués (blip réseau) avant péremption.
 */
export const LOCK_HEARTBEAT_MS = 20_000;

/**
 * Un chatteur qui n'a écrit à AUCUN fan depuis ce délai perd toutes ses
 * discussions (doit matcher `WRITE_IDLE_MS` côté Convex, conversationLocks.ts).
 */
export const WRITE_IDLE_MS = 15 * 60 * 1000;
/** L'avertissement s'affiche 5 min avant la libération (après 10 min sans écrire)… */
export const WRITE_IDLE_WARN_MS = 10 * 60 * 1000;
/** …et repasse en rappel pressant la dernière minute. */
export const WRITE_IDLE_LAST_CALL_MS = 14 * 60 * 1000;

export type IdleWarning =
  | { stage: "warning" | "lastCall"; msLeft: number }
  | null;

/**
 * Où en est le chrono d'écriture du chatteur. `null` = rien à dire : il a
 * écrit récemment, ou on ne sait pas encore (pas de chrono). Pur.
 */
export function idleWarningFor(
  lastWrittenAt: number | null | undefined,
  now: number,
): IdleWarning {
  if (lastWrittenAt == null) return null;
  const silentMs = now - lastWrittenAt;
  if (silentMs < WRITE_IDLE_WARN_MS) return null;
  const msLeft = Math.max(0, WRITE_IDLE_MS - silentMs);
  return {
    stage: silentMs >= WRITE_IDLE_LAST_CALL_MS ? "lastCall" : "warning",
    msLeft,
  };
}

/** « 4:07 » — le temps restant, arrondi à la seconde supérieure. */
export function formatCountdown(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Rôles qui passent outre le verrou ET ne verrouillent jamais (superviseurs). */
export const BYPASS_ROLE_SLUGS = ["ceo", "manager"] as const;

/**
 * True si le rôle peut ouvrir/écrire malgré un verrou d'autrui (et ne pose
 * jamais de verrou bloquant). Superadmin Dozka inclus.
 */
export function isBypassRole(
  roleSlug: string | null | undefined,
  isSuperadmin: boolean,
): boolean {
  return (
    isSuperadmin === true ||
    roleSlug === "ceo" ||
    roleSlug === "manager"
  );
}

/** Verrou sérialisé tel que renvoyé par `conversationLocks.listForAccount`. */
export interface ConvLock {
  conversationId: string;
  lockedByWorkosUserId: string;
  lockedByName: string;
  lockedByAvatarUrl: string | null;
  lockedAt: number;
  expiresAt: number;
}

export type LockStatus = "free" | "mine" | "other";

export interface LockView {
  /** free = pas de verrou actif ; mine = tenu par moi ; other = tenu par un autre. */
  status: LockStatus;
  /** Le verrou actif pertinent, ou null si `free`. */
  lock: ConvLock | null;
  /**
   * True si l'utilisateur courant NE peut PAS ouvrir/écrire : verrou d'un autre
   * ET rôle non-bypass. Les bypass ont toujours `blocked === false`.
   */
  blocked: boolean;
}

/** Un verrou est actif s'il n'est pas périmé selon l'horloge `now`. */
export function isLockActive(
  lock: ConvLock | null | undefined,
  now: number,
): boolean {
  return !!lock && lock.expiresAt > now;
}

/**
 * Décision d'affichage/gating pour UNE conversation.
 *
 * @param lock            verrou courant (ou null/undefined si aucun)
 * @param now             horloge client (pour péremption locale instantanée)
 * @param myWorkosUserId  id WorkOS de l'utilisateur courant (null si non résolu)
 * @param isBypass        true si rôle superviseur (ceo/manager/superadmin)
 */
export function viewLock(
  lock: ConvLock | null | undefined,
  now: number,
  myWorkosUserId: string | null,
  isBypass: boolean,
): LockView {
  if (!isLockActive(lock, now)) {
    return { status: "free", lock: null, blocked: false };
  }
  const active = lock as ConvLock;
  if (myWorkosUserId && active.lockedByWorkosUserId === myWorkosUserId) {
    return { status: "mine", lock: active, blocked: false };
  }
  // Tenu par quelqu'un d'autre : bloquant sauf pour les superviseurs.
  return { status: "other", lock: active, blocked: !isBypass };
}

/**
 * Collecte les `conversationId` de TOUS les onglets « fan » ouverts dans le
 * layout (toutes les TabGroups, tous les splits). C'est l'ensemble qui doit
 * rester verrouillé pour l'utilisateur courant (« dès qu'elle fait partie de
 * ses onglets »). Dédupliqué.
 */
export function collectOpenFanConversationIds(layout: Layout): string[] {
  const ids = new Set<string>();
  const walk = (node: LayoutNode): void => {
    if (node.kind === "tabs") {
      for (const p of node.panels) {
        if (p.panelId !== "fan") continue;
        const cid = p.params?.["conversationId"];
        if (typeof cid === "string" && cid.length > 0) ids.add(cid);
      }
      return;
    }
    for (const child of node.children) walk(child);
  };
  walk(layout.root);
  return [...ids];
}

/**
 * Conversations que l'app « La Régie » affiche en carte focus et souhaite voir
 * verrouillées à son nom, alors même qu'AUCUN onglet `fan` n'est ouvert pour
 * elles. Le `ConversationLockController` unionne cet atom avec les onglets fan
 * ouverts avant de réconcilier les verrous — sans quoi un verrou posé hors
 * onglet fan serait repris au tick suivant (`reconcileLocks` libère tout ce qui
 * n'est pas dans l'ensemble ouvert).
 *
 * Vit ici (module pur du verrou) pour être importable par le controller ET par
 * La Régie sans cycle. Défaut `[]` → comportement STRICTEMENT identique tant
 * que La Régie n'est pas montée.
 */
export const regieFocusConversationIdsAtom = atom<string[]>([]);

/**
 * Union dédupliquée des conversations à maintenir verrouillées pour
 * l'utilisateur courant : onglets `fan` ouverts + cartes focus de La Régie.
 * Pure et testable. Quand `regieFocusIds` est vide, le résultat est exactement
 * l'ensemble des onglets fan (aux doublons près) — invariant de non-régression.
 */
export function unionLockedConversationIds(
  openFanConversationIds: readonly string[],
  regieFocusIds: readonly string[],
): string[] {
  const set = new Set<string>();
  for (const id of openFanConversationIds) {
    if (id.length > 0) set.add(id);
  }
  for (const id of regieFocusIds) {
    if (id.length > 0) set.add(id);
  }
  return [...set];
}

/** Construit une `Map<conversationId, ConvLock>` à partir de la liste Convex. */
export function indexLocksByConversation(
  locks: readonly ConvLock[],
): Map<string, ConvLock> {
  const map = new Map<string, ConvLock>();
  for (const l of locks) map.set(l.conversationId, l);
  return map;
}

/**
 * Extrait le pseudo du teneur d'un ConvexError `ALREADY_LOCKED` (levé par
 * `conversationLocks.acquire`). Retourne "" si la forme n'est pas reconnue
 * (erreur réseau, autre code…) — le caller affiche alors un libellé générique.
 */
export function lockNameFromError(err: unknown): string {
  const data = (err as { data?: unknown } | null | undefined)?.data;
  if (data && typeof data === "object" && "lockedByName" in data) {
    const name = (data as { lockedByName?: unknown }).lockedByName;
    if (typeof name === "string") return name;
  }
  return "";
}

/**
 * Le plafond de `conversationLocks.acquire` (`TOO_MANY_LOCKS`, 28-10-2026) :
 * le nombre max de discussions tenues à la fois, ou `null` si ce n'est pas ce
 * refus-là.
 */
export function plafondFromError(err: unknown): number | null {
  const data = (err as { data?: unknown } | null | undefined)?.data;
  if (data && typeof data === "object" && (data as { code?: unknown }).code === "TOO_MANY_LOCKS") {
    const max = (data as { max?: unknown }).max;
    return typeof max === "number" ? max : 0;
  }
  return null;
}
